import {
  Button,
  HStack,
  Input,
  NativeSelect,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { Checkbox, Field, Modal, text } from '@mangovoice/mango-ui'

import type { ScheduleTranslationLike } from '../../i18n'
import type { ScheduleCreate, ScheduleModalNotification } from '../../types'
import {
  CURATED_SCHEDULE_TIMEZONE_OPTIONS,
  dsText,
  fontSize,
  type ScheduleTimeZoneOption,
  tokenVar,
} from '../../utils'
import {
  createScheduleModalNotifier,
  scheduleModalInputStyles,
  scheduleModalInvalidInputStyles,
  scheduleModalLabelStyles,
} from '../shared'

export interface CreateScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  pbxId: number
  onSave: (data: ScheduleCreate, defaultWeeklyHours: boolean) => void
  isSaving?: boolean
  t?: ScheduleTranslationLike
  onNotify?: (notification: ScheduleModalNotification) => void
  timeZoneOptions?: ScheduleTimeZoneOption[]
  initialTimeZone?: string
}

interface FormState {
  name: string
  description: string
  timeZone: string
  defaultWeeklyHours: boolean
}

const FALLBACK_TIME_ZONE = 'America/New_York'

const resolveInitialTimeZone = (
  initialTimeZone: string | undefined,
): string => {
  const normalized = initialTimeZone?.trim()
  return normalized ? normalized : FALLBACK_TIME_ZONE
}

const createDefaultFormState = (initialTimeZone: string): FormState => ({
  name: '',
  description: '',
  timeZone: initialTimeZone,
  defaultWeeklyHours: true,
})

const mergeTimeZoneOptions = (
  options: ScheduleTimeZoneOption[],
  initialTimeZone: string,
): ScheduleTimeZoneOption[] => {
  const merged = [...options]
  const hasInitialTimeZone = options.some(
    (timezone) => timezone.value === initialTimeZone,
  )

  if (!hasInitialTimeZone) {
    merged.push({
      value: initialTimeZone,
      label: initialTimeZone,
    })
  }

  return merged
}

export const CreateScheduleModal = ({
  isOpen,
  onClose,
  pbxId,
  onSave,
  isSaving = false,
  t,
  onNotify,
  timeZoneOptions = CURATED_SCHEDULE_TIMEZONE_OPTIONS,
  initialTimeZone,
}: CreateScheduleModalProps) => {
  const resolvedInitialTimeZone = useMemo(
    () => resolveInitialTimeZone(initialTimeZone),
    [initialTimeZone],
  )
  const mergedTimeZoneOptions = useMemo(
    () => mergeTimeZoneOptions(timeZoneOptions, resolvedInitialTimeZone),
    [timeZoneOptions, resolvedInitialTimeZone],
  )
  const [formState, setFormState] = useState<FormState>(() =>
    createDefaultFormState(resolvedInitialTimeZone),
  )
  const [hasTouchedTimeZone, setHasTouchedTimeZone] = useState(false)
  const [nameError, setNameError] = useState<{ message: string } | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const nameErrorId = useId()

  useEffect(() => {
    if (nameError) nameInputRef.current?.focus()
  }, [nameError])
  const wasOpenRef = useRef(false)
  const toaster = useMemo(
    () => createScheduleModalNotifier(onNotify),
    [onNotify],
  )

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen

    if (justOpened) {
      setFormState(createDefaultFormState(resolvedInitialTimeZone))
      setHasTouchedTimeZone(false)
      setNameError(null)
    }
  }, [isOpen, resolvedInitialTimeZone])

  useEffect(() => {
    if (!isOpen || hasTouchedTimeZone) return

    setFormState((prev) => {
      if (prev.timeZone === resolvedInitialTimeZone) return prev
      return { ...prev, timeZone: resolvedInitialTimeZone }
    })
  }, [isOpen, hasTouchedTimeZone, resolvedInitialTimeZone])

  const updateField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormState((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const handleTimeZoneChange = useCallback(
    (value: string) => {
      setHasTouchedTimeZone(true)
      updateField('timeZone', value)
    },
    [updateField],
  )

  const handleSave = useCallback(() => {
    if (!formState.name.trim()) {
      const message = text(
        t,
        'schedule.modal.create.validationNameRequired',
        'Please enter a name for the schedule',
      )
      setNameError({ message })
      toaster.create({
        title: text(t, 'schedule.modal.create.validationTitle', 'Validation'),
        description: message,
        type: 'error',
      })
      return
    }

    const createData: ScheduleCreate = {
      name: formState.name.trim(),
      description: formState.description.trim() || undefined,
      pbx_id: pbxId,
      time_zone: formState.timeZone,
    }

    onSave(createData, formState.defaultWeeklyHours)
  }, [formState, onSave, pbxId, t, toaster])

  return (
    <Modal
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      title={text(t, 'schedule.modal.create.title', 'New Schedule')}
      maxWidth="500px"
      footer={
        <HStack gap={3} w="full">
          <Button
            variant="outline"
            onClick={onClose}
            flex={1}
            h="44px"
            borderColor={tokenVar('border.default')}
            bg={tokenVar('bg.default')}
            color={tokenVar('fg.default')}
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
            _hover={{ bg: tokenVar('gray.subtle') }}
          >
            {text(t, 'schedule.modal.create.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSave}
            disabled={isSaving}
            flex={1}
            h="44px"
            borderRadius="4px"
            fontFamily={dsText.Body1_Bold.fontFamily}
            fontSize={fontSize(dsText.Body1_Bold.fontSize)}
            fontWeight={dsText.Body1_Bold.fontWeight}
            lineHeight={dsText.Body1_Bold.lineHeight}
          >
            {isSaving
              ? text(t, 'schedule.modal.create.saving', 'Saving...')
              : text(t, 'schedule.modal.create.saveSchedule', 'Save Schedule')}
          </Button>
        </HStack>
      }
    >
      <VStack gap={5} align="stretch">
        <Field.Root
          required
          invalid={!!nameError}
          ids={{ errorText: nameErrorId }}
          gap={2}
        >
          <Field.Label style={scheduleModalLabelStyles}>
            {text(t, 'schedule.modal.create.nameLabel', 'Name')}
          </Field.Label>
          <Input
            ref={nameInputRef}
            aria-describedby={nameError ? nameErrorId : undefined}
            value={formState.name}
            onChange={(event) => {
              updateField('name', event.target.value)
              setNameError(null)
            }}
            placeholder={text(
              t,
              'schedule.modal.create.namePlaceholder',
              'Business Hours',
            )}
            px={3}
            {...scheduleModalInputStyles}
            {...scheduleModalInvalidInputStyles}
          />
          {nameError && <Field.ErrorText>{nameError.message}</Field.ErrorText>}
        </Field.Root>

        <Field.Root gap={2}>
          <Field.Label style={scheduleModalLabelStyles}>
            {text(t, 'schedule.modal.create.descriptionLabel', 'Description')}
          </Field.Label>
          <Textarea
            value={formState.description}
            onChange={(event) => updateField('description', event.target.value)}
            placeholder={text(
              t,
              'schedule.modal.create.descriptionPlaceholder',
              'Optional description for this schedule',
            )}
            rows={3}
            px={3}
            {...scheduleModalInputStyles}
          />
        </Field.Root>

        <Field.Root required gap={2}>
          <Field.Label style={scheduleModalLabelStyles}>
            {text(t, 'schedule.modal.create.timeZoneLabel', 'Time Zone')}
          </Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={formState.timeZone}
              onChange={(event) => handleTimeZoneChange(event.target.value)}
              px={3}
              {...scheduleModalInputStyles}
            >
              {mergedTimeZoneOptions.map((timezone) => (
                <option key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator color={tokenVar('fg.muted')} />
          </NativeSelect.Root>
        </Field.Root>

        <Checkbox
          checked={formState.defaultWeeklyHours}
          onCheckedChange={(details) =>
            updateField('defaultWeeklyHours', !!details.checked)
          }
          display="inline-flex"
          alignItems="center"
          gap={0}
        >
          <Text as="span" style={scheduleModalLabelStyles}>
            {text(
              t,
              'schedule.modal.create.defaultWeeklyHoursLabel',
              'Include default weekly hours',
            )}
          </Text>
        </Checkbox>
      </VStack>
    </Modal>
  )
}
